// ./src/components/features/Donations/DonationsList/DonationsList.tsx
//
// ROADMAP #204 — the account's gifts. A TABLE on desktop (received · donor · cause · amount · state · receipt · open), CARDS
// below it (swipe on phones, a button on a tablet). The row's one action is "Open" — the gift's drawer holds its receipt,
// refund and void, each with its own button. Rows are never click-to-open (tap-safe). Shape of `CausesList`.
import React, { useCallback, useMemo } from "react";
import { Paper, Stack, Group, Text, Box, Badge, ActionIcon, Tooltip, Skeleton, Table, VisuallyHidden } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Donation } from "@/utils/api";
import { useAppEnvironment } from "@/contexts";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  countCardActions,
  TableDateTime,
  type CardSkeletonShape,
} from "@/components/generic";
import { formatCurrency, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";

export interface DonationsListProps {
  donations: Donation[];
  /** REQUIRED — the list takes its rows only as props. */
  isLoaded: boolean;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onOpen: (gift: Donation) => void;
  /** Recording a gift by hand — the first-run empty state's action. */
  onRecord?: () => void;
  highlightedId?: string | null;
  /** A search or a filter is narrowing the list (an empty result is "no match"). */
  hasQueryNarrowing: boolean;
  /** Cause names by id. */
  causeNames: Map<string, string>;
}

const STATUS_COLOR: Record<string, string> = { received: "green", refunded: "orange", void: "gray", pending: "blue" };

export const useDonationLabels = () => {
  const { t } = useTranslation(["donations"]);

  const statusLabel = useCallback(
    (s?: string) =>
      s === "received"
        ? t("donations:status.received", { defaultValue: "Received" })
        : s === "refunded"
          ? t("donations:status.refunded", { defaultValue: "Refunded" })
          : s === "void"
            ? t("donations:status.void", { defaultValue: "Void" })
            : t("donations:status.pending", { defaultValue: "Pending" }),
    [t]
  );

  const methodLabel = useCallback(
    (m?: string | null) =>
      m === "card"
        ? t("donations:method.card", { defaultValue: "Card, online" })
        : m === "cash"
          ? t("donations:method.cash", { defaultValue: "Cash" })
          : m === "cheque"
            ? t("donations:method.cheque", { defaultValue: "Cheque" })
            : m === "terminal"
              ? t("donations:method.terminal", { defaultValue: "Card terminal" })
              : m === "transfer"
                ? t("donations:method.transfer", { defaultValue: "Bank transfer" })
                : t("donations:method.other", { defaultValue: "Other" }),
    [t]
  );

  return { statusLabel, methodLabel };
};

/** The receipt column: the issued receipt's number, else "None". */
const receiptOf = (g: Donation): string | null => g.receipts?.find((r) => r.receiptState === "issued")?.receiptNumber ?? null;

const DonorCell: React.FC<{ gift: Donation }> = ({ gift }) => {
  const { t } = useTranslation(["donations"]);

  return (
    <Box style={{ minWidth: 0 }}>
      <Group gap={6} wrap="wrap">
        <Text size="md" fw={500} lineClamp={1}>
          {gift.donorName || gift.donorEmail || "—"}
        </Text>
        {gift.isAnonymous && (
          <Badge size="sm" variant="light" color="gray">
            {t("donations:list.anonymous", { defaultValue: "Anonymous" })}
          </Badge>
        )}
        {gift.isTest && (
          <Badge size="sm" variant="light" color="yellow">
            {t("donations:list.test", { defaultValue: "Test" })}
          </Badge>
        )}
      </Group>
      {gift.donorName && gift.donorEmail && (
        <Text size="sm" c="dimmed" lineClamp={1}>
          {gift.donorEmail}
        </Text>
      )}
    </Box>
  );
};

const OpenButton: React.FC<{ gift: Donation; onOpen: (g: Donation) => void }> = ({ gift, onOpen }) => {
  const { t } = useTranslation(["donations"]);
  const label = t("donations:list.open", { defaultValue: "Open gift" });

  return (
    <Tooltip label={label} withArrow position="bottom">
      <ActionIcon variant="subtle" color="blue" size="md" onClick={() => onOpen(gift)} aria-label={label}>
        <AppIcon iconName="ChevronRight" size={16} />
      </ActionIcon>
    </Tooltip>
  );
};

interface RowProps {
  gift: Donation;
  onOpen: (g: Donation) => void;
  highlighted: boolean;
  language: string;
  causeName: string;
  statusText: string;
  methodText: string;
}

const DonationTableRow: React.FC<RowProps> = ({ gift, onOpen, highlighted, language, causeName, statusText, methodText }) => (
  <Table.Tr style={{ backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : undefined, opacity: gift.donationStatus === "void" ? 0.6 : 1 }}>
    <Table.Td style={{ width: 170 }}>
      <TableDateTime value={gift.receivedAt} />
      {/* #204 — the gift's number, unique across the account. */}
      {gift.donationNumber && (
        <Text size="md" c="dimmed" ff="monospace">
          #{gift.donationNumber}
        </Text>
      )}
    </Table.Td>
    <Table.Td>
      <DonorCell gift={gift} />
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <Text size="md" lineClamp={1}>
        {causeName}
      </Text>
      <Text size="sm" c="dimmed">
        {methodText}
      </Text>
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Text size="md" fw={500}>
        {formatCurrency(gift.amount ?? 0, gift.currencyCode, language)}
      </Text>
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <Badge variant="light" color={STATUS_COLOR[gift.donationStatus ?? ""] ?? "gray"} size="md">
        {statusText}
      </Badge>
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Text size="md" ff="monospace">
        {receiptOf(gift) ?? "—"}
      </Text>
    </Table.Td>
    <Table.Td style={{ width: 56 }}>
      <OpenButton gift={gift} onOpen={onOpen} />
    </Table.Td>
  </Table.Tr>
);

const DonationTableRowSkeleton: React.FC = () => (
  <Table.Tr>
    <Table.Td style={{ width: 170 }}>
      <Skeleton height={16} width={110} />
      <Skeleton height={14} width={70} mt={4} />
    </Table.Td>
    <Table.Td>
      <Skeleton height={18} width="55%" maw={240} />
      <Skeleton height={14} width="40%" maw={180} mt={4} />
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <Skeleton height={16} width={140} />
      <Skeleton height={14} width={80} mt={4} />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Skeleton height={18} width={80} />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <Skeleton height={22} width={80} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Skeleton height={16} width={70} />
    </Table.Td>
    <Table.Td style={{ width: 56 }}>
      <Skeleton circle height={28} width={28} />
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const DonationCardComponent: React.FC<CardProps> = (props) => {
  const { gift, onOpen, highlighted, language, causeName, statusText, methodText, forceClose, onForceCloseComplete, onSwipeStateChange } = props;
  const { t } = useTranslation(["donations"]);

  // "Open" is the row's identity action — the right side (swipe the card left).
  const swipeActions = useMemo((): SwipeAction[] => [{ key: "open", icon: "ChevronRight", label: t("donations:list.openShort", { defaultValue: "Open" }), color: "blue", onClick: () => onOpen(gift) }], [gift, onOpen, t]);

  const cardBody = (showActions: boolean) => (
    <Paper p="md" withBorder radius="md" opacity={gift.donationStatus === "void" ? 0.6 : 1} style={highlighted ? HIGHLIGHT_STYLE : undefined}>
      <Group gap="md" wrap="nowrap" align="flex-start">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <DonorCell gift={gift} />
            <Text size="md" fw={600}>
              {formatCurrency(gift.amount ?? 0, gift.currencyCode, language)}
            </Text>
          </Group>
          <Text size="sm" c="dimmed" lineClamp={1}>
            {gift.donationNumber ? `#${gift.donationNumber} · ` : ""}
            {causeName} · {methodText}
          </Text>
          <Group gap="xs" mt={4} wrap="wrap">
            <Badge variant="light" color={STATUS_COLOR[gift.donationStatus ?? ""] ?? "gray"} size="sm">
              {statusText}
            </Badge>
            {receiptOf(gift) && (
              <Text size="md" ff="monospace">
                {receiptOf(gift)}
              </Text>
            )}
            <TableDateTime value={gift.receivedAt} inline />
          </Group>
        </Box>
        {showActions && <OpenButton gift={gift} onOpen={onOpen} />}
      </Group>
    </Paper>
  );

  return (
    <>
      <Box hiddenFrom="sm">
        <SwipeableCard
          leftSwipeActions={swipeActions}
          forceClose={forceClose}
          onForceCloseComplete={onForceCloseComplete}
          onSwipeStateChange={onSwipeStateChange}
          borderRadius="var(--mantine-radius-md)"
        >
          {cardBody(false)}
        </SwipeableCard>
      </Box>
      <Box visibleFrom="sm">{cardBody(true)}</Box>
    </>
  );
};

const DonationCard = React.memo(DonationCardComponent);

const DonationCardSkeleton: React.FC<CardSkeletonShape> = ({ actions }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <Box style={{ flex: 1, minWidth: 0 }}>
        <CardSkeletonLines count={3} sizes={["md", "sm", "sm"]} gap={4} />
      </Box>
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

const DonationsListComponent: React.FC<DonationsListProps> = ({ donations, isLoaded, loading, loadingMore, hasMore, onLoadMore, onOpen, onRecord, highlightedId, hasQueryNarrowing, causeNames }) => {
  const { t, i18n } = useTranslation(["donations", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;
  const { statusLabel, methodLabel } = useDonationLabels();
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleOpen = useCallback(
    (g: Donation) => {
      closeAllCards();
      onOpen(g);
    },
    [onOpen, closeAllCards]
  );
  // The tablet card's row buttons — exactly the handlers its rows get (▶ check-card-skeletons).
  const cardActions = countCardActions(handleOpen);

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY (▶ check-list-states).
  const showSkeleton = !isLoaded;

  if (!showSkeleton && donations.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("donations:list.noMatch", { defaultValue: "No gifts match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("donations:list.empty.title", { defaultValue: "No gifts yet" })}
          description={t("donations:list.empty.description", {
            defaultValue: "Gifts given on your organizer pages appear here, with their receipts. A cheque or cash gift can be recorded by hand.",
          })}
          iconName="Heart"
          actionLabel={onRecord ? t("donations:list.empty.action", { defaultValue: "Record a gift" }) : undefined}
          onAction={onRecord}
          size="md"
        />
      </Paper>
    );
  }

  const rowProps = (g: Donation): RowProps => ({
    gift: g,
    onOpen: handleOpen,
    highlighted: g.id === highlightedId,
    language,
    causeName: (g.causeId && causeNames.get(g.causeId)) || "—",
    statusText: statusLabel(g.donationStatus),
    methodText: methodLabel(g.paymentMethod),
  });

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 170 }}>{t("donations:list.columns.received", { defaultValue: "Received" })}</Table.Th>
                <Table.Th>{t("donations:list.columns.donor", { defaultValue: "Donor" })}</Table.Th>
                <Table.Th style={{ width: 200 }}>{t("donations:list.columns.cause", { defaultValue: "Cause" })}</Table.Th>
                <Table.Th style={{ width: 140 }}>{t("donations:list.columns.amount", { defaultValue: "Amount" })}</Table.Th>
                <Table.Th style={{ width: 120 }}>{t("donations:list.columns.status", { defaultValue: "Status" })}</Table.Th>
                <Table.Th style={{ width: 140 }}>{t("donations:list.columns.receipt", { defaultValue: "Receipt" })}</Table.Th>
                <Table.Th style={{ width: 56 }}>
                  <VisuallyHidden>{t("common:labels.actions", { defaultValue: "Actions" })}</VisuallyHidden>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton ? Array.from({ length: 6 }).map((_, i) => <DonationTableRowSkeleton key={i} />) : donations.map((g) => <DonationTableRow key={g.id} {...rowProps(g)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 5 }).map((_, i) => (
          <DonationCardSkeleton key={i} actions={cardActions} withCheckbox={false} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      {donations.map((g) => (
        <DonationCard
          key={g.id}
          {...rowProps(g)}
          forceClose={shouldForceClose(g.id || "")}
          onForceCloseComplete={() => handleForceCloseComplete(g.id || "")}
          onSwipeStateChange={(isOpen) => {
            if (isOpen) {
              openCard(g.id || "");
            }
          }}
        />
      ))}
      {loadMore}
    </Stack>
  );
};

export const DonationsList = React.memo(DonationsListComponent);
